import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import type { BuyPackButton_cardPack } from './__generated__/index.graphql';

const StyledButton = styled(Button)`
  &.medium {
    min-width: 90px;
  }
  &.large {
    min-width: 100px;
  }
`;
type Props = Omit<Partial<ComponentProps<typeof Button>>, 'children'> & {
  cardPack: Nullable<BuyPackButton_cardPack>;
  children?: (price: ReactNode) => ReactNode;
};

export const BuyPackButton = ({
  cardPack,
  children,
  onClick,
  ...otherProps
}: Props) => {
  const track = useEvents();

  const isGemsPack = cardPack?.currency === 'COMMON_GEM';
  const currencyIcon = isGemsPack ? (
    <GemIcon size={2.5} />
  ) : (
    <Shards size={2} />
  );
  const price = (
    <>
      {cardPack?.price} {currencyIcon}
    </>
  );
  return (
    <StyledButton
      size="large"
      color="primary"
      {...otherProps}
      onClick={
        onClick
          ? e => {
              onClick(e);
              if (cardPack) {
                track('Click Buy Pack', {
                  packSlug: cardPack?.slug,
                });
              }
            }
          : undefined
      }
    >
      <Horizontal center>{children?.(price) || price}</Horizontal>
    </StyledButton>
  );
};
BuyPackButton.fragments = {
  cardPack: gql`
    fragment BuyPackButton_cardPack on CardPack {
      slug
      currency
      price
    }
  ` as TypedDocumentNode<BuyPackButton_cardPack>,
};
